
import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Pagination,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExportDropdown from "../../../../components/ExportDropdown.js";
import CustomText, { textStyles } from "../../../../components/CustomText.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import { useTheme } from "../../../../contexts/ThemeContext";// ✅ needed since token is stored encrypted
import { showUnderDevelopment } from "../../../../components/Alert.js";
import TruncatedCell from "../../../../components/TruncatedCell.js";
// 👉 your assets (adjust paths/names if needed)
import document1 from "../../../../assets/document1.svg"; // pdf icon
import document2 from "../../../../assets/document2.svg"; // image/doc icon
import empty from "../../../../assets/empty.png";
import AddDocDrawer from "./AddDrawer";
import AddIcon from "@mui/icons-material/Add";
import ViewDrawer from "./ViewDrawer.js";
import EditDrawer from "./EditDrawer.js";
import FilterDrawer from "./FilterDrawer.js";
import ExportCompanyDocsExcel from "./companyExcel.js";
import ExportCompanyDocsPDF from "./companyPdf.js";
import { pdf } from "@react-pdf/renderer";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useCompanyProfile } from "../../../../components/CompanyProfile";
import { toast } from "react-hot-toast";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils.js";

// ---- shared styles
const headerStyle = {
  color: "#44546F",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

const dataStyle = {
  color: "#172B4D",
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

// ---- helpers
const formatDateTime = (iso) => {
  const formatted = convertUTCToLocalDateTime(iso, {
    locale: "en-GB",
    formatOptions: {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  });
  return formatted === "—" ? "-" : formatted;
};

const fileTypeIcon = (fileType) => {
  const ft = (fileType || "").toLowerCase();
  if (ft.includes("pdf")) return document1;
  return document2;
};

export default function CompanyDocuments() {
  const { t } = useTranslation();
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();

  // ---- state
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // pagination (from backend)
  const [page, setPage] = useState(1);
  const [limit] = useState(5); // adjust as needed
  const [totalPages, setTotalPages] = useState(1);

const company = useSelector((state) => state.auth.company);
const companyId = company?.id;
const [loading, setLoading] = useState(true);      // first full load
const [tableLoading, setTableLoading] = useState(false); 
// console.log("Redux coany object:", company);
// console.log("Company ID:", companyId);mp
const [addDrawerOpen, setAddDrawerOpen] = useState(false);
const [editDrawerOpen, setEditDrawerOpen] = useState(false);
const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
const [isFilterApplied, setIsFilterApplied] = useState(false);

const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
const [viewRow, setViewRow] = useState(null);
const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
const [deleting, setDeleting] = useState(false);
 const token = useSelector((state) => state.auth.token);
   const decryptedToken = token ? decryptToken(token) : null;

  // Filters state
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    status: "",
    sortBy: "created_at",
    sortOrder: "desc",
  });



const fetchDocs = async () => {
  if (!companyId || !token) return;

  if (rows.length === 0) setLoading(true);
  else setTableLoading(true);

  try {
    const queryParams = new URLSearchParams({
      limit: String(limit),
      page: String(page),
      company_id: String(companyId),
      search: search || "",
    });
    // Backend-supported filters (documents.service.js getCompanyDocuments):
    // search, status, category, date_from, date_to, sort_by, sort_order.
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.startDate) queryParams.append("date_from", filters.startDate);
    if (filters.endDate) queryParams.append("date_to", filters.endDate);
    if (filters.sortBy) queryParams.append("sort_by", filters.sortBy);
    if (filters.sortOrder) queryParams.append("sort_order", filters.sortOrder);

    const res = await fetch(
      `${BASE_URL}/documents/company?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      }
    );

    if (!res.ok) throw new Error("Failed to fetch documents");

    const data = await res.json();
    const mapped = (data?.data?.documents || []).map((doc) => ({
      id: doc.id,
      company_id: doc.company_id,
      direction: doc.direction,
      owner_type: doc.owner_type,
      owner_id: doc.owner_id,
      name: doc.name,
      documentName: doc.name,
      subject: doc.subject,
      description: doc.description,
      file_id: doc.file_id,
      fileType: doc.file_type,
      file_type: doc.file_type,
      category: doc.category,
      access_mode: doc.access_mode,
      status: doc.status,
      expires_at: doc.expires_at,
      created_at: doc.created_at,
      createdAt: doc.created_at,
      updated_at: doc.updated_at,
      deleted_at: doc.deleted_at,
      document_url: doc.document_url,
      fileUrl: doc.document_url || doc.file_id, // Use document_url if available, fallback to file_id
      has_access: doc.has_access,
      recipient_worker_ids: doc.recipient_worker_ids || [],
      owner: doc.owner || null,
    }));
    console.log("📊 Mapped documents data:", mapped);
    setRows(mapped);
    setTotalPages(data?.data?.pagination?.pages || 1);
  } catch (err) {
    console.error("❌ Error fetching documents:", err);
  } finally {
    setLoading(false);
    setTableLoading(false);
  }
};
// run on mount / pagination / search / filters
useEffect(() => {
  fetchDocs();
}, [companyId, decryptedToken, page, limit, search, filters]);


  // ---- selection
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));


  // ---- row menu
  const openMenu = (e, row) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const closeMenu = () => setAnchorEl(null);

  // Filter handlers
  const handleApplyFilters = (newFilters) => {
    console.log("📌 Applying new filters:", newFilters);
    setFilters(newFilters);
    setPage(1);

    // Check if any filter is applied — sortBy/sortOrder always carry a value
    // (they default to "created_at"/"desc"), so only count them as "applied"
    // when they differ from those defaults.
    const hasFilters =
      newFilters.startDate ||
      newFilters.endDate ||
      newFilters.status ||
      (newFilters.sortBy && newFilters.sortBy !== "created_at") ||
      (newFilters.sortOrder && newFilters.sortOrder !== "desc");
    setIsFilterApplied(Boolean(hasFilters));
  };

  const clearFilters = () => {
    const cleared = {
      startDate: "",
      endDate: "",
      status: "",
      sortBy: "created_at",
      sortOrder: "desc",
    };
    setFilters(cleared);
    setPage(1);
    setIsFilterApplied(false);
  };

  // Delete handler
  const handleDelete = async () => {
    if (!selectedRow) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`${BASE_URL}/documents/${selectedRow.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.message || data.error || "Failed to delete document");
      }

      toast.success(data.message || "Document deleted successfully");
      setDeleteDialogOpen(false);
      fetchDocs(); // Refresh the list
    } catch (err) {
      console.error("❌ Error deleting document:", err);
      toast.error(err.message || "Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };



 const fetchAllCompanyDocs = async () => {
  try {
    const queryParams = new URLSearchParams({
      no_pagination: "true",
      company_id: companyId,
      ...(search && { search }),
      ...(filters.startDate && { date_from: filters.startDate }),
      ...(filters.endDate && { date_to: filters.endDate }),
      ...(filters.status && { status: filters.status }),
      sort_by: filters.sortBy || "created_at",
      sort_order: filters.sortOrder || "desc",
    });

    const url = `${BASE_URL}/documents/company?${queryParams.toString()}`;
    console.log("📡 Fetching ALL company docs:", url);

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${decryptedToken}` },
    });

    const json = await res.json();
    if (json.error) {
      console.error("❌ Export error:", json.message);
      return [];
    }
    
    console.log("json data", json);
    const allDocs = (json?.data?.documents || []).map((doc) => ({
      id: doc.id,
      company_id: doc.company_id,
      direction: doc.direction,
      owner_type: doc.owner_type,
      owner_id: doc.owner_id,
      name: doc.name,
      documentName: doc.name,
      subject: doc.subject,
      description: doc.description,
      file_id: doc.file_id,
      fileType: doc.file_type,
      file_type: doc.file_type,
      category: doc.category,
      access_mode: doc.access_mode,
      status: doc.status,
      expires_at: doc.expires_at,
      created_at: doc.created_at,
      createdAt: formatDateTime(doc.created_at),
      updated_at: doc.updated_at,
      deleted_at: doc.deleted_at,
      document_url: doc.document_url,
      has_access: doc.has_access,
      recipient_worker_ids: doc.recipient_worker_ids || [],
      owner: doc.owner || null,
    }));

    return allDocs;

  } catch (err) {
    console.error("❌ fetchAllCompanyDocs error:", err);
    return [];
  }
};

const [downloadingId, setDownloadingId] = useState(null);
const { logoBase64 } = useCompanyProfile(decryptedToken);
  return (
    <>
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        border: "2px solid #091E4224",
        py: 2,
        px: 0,
        ml: 1,
        mr: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          px: 2,
          borderBottom: "2px solid #091E4224",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
            textAlign: "left",
          }}
        >
          {t("documentsTable.companyDocuments") || "Received Documents"}
        </CustomText>
          {tableLoading && (
    <CircularProgress size={20} sx={{ color: primaryColor }} />
  )}
</Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 1,
              height: 34,
              border: "2px solid #091E4224",
              display: "flex",
              alignItems: "center",
              minWidth: 250,
              maxWidth: 320,
            }}
          >
            <InputBase
              placeholder={t("documentsTable.searchPlaceholder") || "Search document..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                color: "#939393",
                width: "100%",
              }}
            />
            <SearchIcon sx={{ fontSize: 20, color: "#888", ml: 1 }} />
          </Box>

         <ExportDropdown
  onDownloadPDF={async () => {
    const allDocs = await fetchAllCompanyDocs();
    const blob = await pdf(<ExportCompanyDocsPDF data={allDocs} logo={logoBase64} />).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Company_Documents ${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`;
    link.click();
  }}
  onDownloadExcel={async () => {
    const allDocs = await fetchAllCompanyDocs();
    ExportCompanyDocsExcel(allDocs);
  }}
/>

          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Button
              onClick={() => setFilterDrawerOpen(true)}
              variant="outlined"
              size="small"
              sx={{
                px: 2,
                border: `2px solid ${isFilterApplied ? primaryColor : "#091E4224"}`,
                color: isFilterApplied ? primaryColor : "#172B4D",
                height: 32,
                fontSize: 14,
                fontWeight: 400,
                fontFamily: "Poppins, sans-serif",
                borderRadius: "4px",
                textTransform: "none",
                backgroundColor: isFilterApplied ? primaryColor + '10' : "transparent",
                '&:hover': {
                  backgroundColor: isFilterApplied ? primaryColor + '20' : "#f5f5f5",
                  border: `2px solid ${primaryColor}`,
                },
              }}
              startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            >
              {t("common.filter") || "Filter"}
            </Button>
            {isFilterApplied && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  bgcolor: "white",
                  width: 18,
                  height: 18,
                  border: "1px solid #C4C4C4",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14, color: "#333" }} />
              </IconButton>
            )}
          </Box>

                  <Button
                              onClick={() => setAddDrawerOpen(true)}
            variant="contained"
            size="small"
            sx={{
              px: 2,
              backgroundColor: primaryColor,
              color: primaryTextColor,
              height: 32,
              fontSize: 14,
              fontWeight: 400,
              fontFamily: "Poppins, sans-serif",
              borderRadius: "4px",
              textTransform: "none",
              '&:hover': {
                backgroundColor: secondaryColor,
                color: secondaryTextColor,
              },
              '& .MuiSvgIcon-root': {
                color: 'inherit',
              },
            }}
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          >
            {t("common.add") || "Add"}
          </Button>
        </Box>
      </Box>

      {/* Table */}

      {loading ? (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
    <CircularProgress size={40} sx={{ color: primaryColor }} />
  </Box>
) : rows.length === 0 ? (
  // Empty state
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      py: 5,
    }}
  >
    <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
    <Typography sx={{ fontSize: "15px",        fontFamily: 'Poppins, sans-serif',
 }}>
      {t("attendanceSettings.noWorkers")}
    </Typography>
  </Box>
) : (
  // Table + Pagination + Menu
  <>
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyle}>{t("documentsTable.th.id") || "ID"}</TableCell>
              <TableCell sx={headerStyle}>
                {t("documentsTable.th.subject") || "Subject"}
              </TableCell>
              <TableCell sx={headerStyle}>
                {t("documentsTable.th.documentName") || "Document Name"}
              </TableCell>
            <TableCell sx={headerStyle}>{t("documentsTable.th.description")}</TableCell>
              {/* <TableCell sx={headerStyle}>Category</TableCell> */}
              <TableCell sx={headerStyle}>{t("documentsTable.th.accessMode")}</TableCell>
              <TableCell sx={headerStyle}>{t("documentsTable.th.status")}</TableCell>
              <TableCell sx={headerStyle}>{t("documentsTable.th.recipients")}</TableCell>
              <TableCell sx={headerStyle}>{t("documentsTable.th.expiresAt")}</TableCell>
              <TableCell sx={headerStyle}>
                {t("documentsTable.th.fileType") || "File Type"}
              </TableCell>
              <TableCell sx={headerStyle}>
                {t("documentsTable.th.dateTime") || "Date - Time"}
              </TableCell>
              <TableCell sx={headerStyle}>{t("documentsTable.th.updatedAt")}</TableCell>
              <TableCell sx={headerStyle}>{t("common.action") || "Action"}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={dataStyle}>{row.id}</TableCell>
                <TableCell sx={dataStyle}><TruncatedCell text={row.subject || "--"} /></TableCell>
                <TableCell sx={dataStyle}><TruncatedCell text={row.documentName || row.name} /></TableCell>
                <TableCell sx={dataStyle}><TruncatedCell text={row.description || "--"} /></TableCell>
                {/* <TableCell sx={dataStyle}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "Poppins, sans-serif",
                      backgroundColor: "#F5F5F5",
                      color: "#5E5C5C",
                      display: "inline-block",
                    }}
                  >
                    {row.category || "--"}
                  </Box>
                </TableCell> */}
                <TableCell sx={dataStyle}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "Poppins, sans-serif",
                      backgroundColor: row.access_mode === "all" ? "#E3F2FD" : "#FFF3E0",
                      color: row.access_mode === "all" ? "#1976D2" : "#E65100",
                      textTransform: "capitalize",
                      display: "inline-block",
                    }}
                  >
                   {row.access_mode
? t(`accessMode.${row.access_mode}`)
: "--"}
                  </Box>
                </TableCell>
                <TableCell sx={dataStyle}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "Poppins, sans-serif",
                      backgroundColor: row.status === "active" ? "#E8F5E9" : "#FFEBEE",
                      color: row.status === "active" ? "#2E7D32" : "#C62828",
                      textTransform: "capitalize",
                      display: "inline-block",
                    }}
                  >
                 {row.status
? t(`statuses.${row.status}`, row.status)
: "--"}
                  </Box>
                </TableCell>
                <TableCell sx={dataStyle}>
                  {row.access_mode === "all"
                    ? t("documentsTable.filter.allEmployees")
                    : (row.recipient_worker_ids && row.recipient_worker_ids.length > 0
                        ? t("documentsTable.filter.recipientsCount", { count: row.recipient_worker_ids.length })
                        : "--")}
                </TableCell>
                <TableCell sx={dataStyle}>
                  {row.expires_at ? formatDateTime(row.expires_at) : "--"}
                </TableCell>

                {/* <TableCell sx={dataStyle}>
                  <Tooltip title={(row.fileType || "").toUpperCase()}>
                    <Box
                      component="img"
                      src={fileTypeIcon(row.fileType)}
                      alt={row.fileType || "file"}
                      sx={{ width: 22, height: 22, display: "block" }}
                    />
                  </Tooltip>
                </TableCell> */}
  <TableCell sx={dataStyle}>
  {(row.fileType || row.file_type)?.toLowerCase().includes("pdf") ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* PDF icon */}
      <Tooltip title="PDF">
        <Box
          component="img"
          src={document1}
          alt="pdf"
          sx={{ width: 22, height: 22 }}
        />
      </Tooltip>

      {/* Download button with loader */}
      <Tooltip title="Download PDF">
        <IconButton
          size="small"
          onClick={async () => {
            const downloadUrl = row.document_url || row.fileUrl || row.file_id;
            if (!downloadUrl) return;

            try {
              setDownloadingId(row.id); // 👈 start loader

              const res = await fetch(downloadUrl);
              if (!res.ok) throw new Error("Failed to download file");
              const blob = await res.blob();

              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = (row.documentName || row.name || "document") + ".pdf";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              console.log("✅ Download started:", row.documentName || row.name);
            } catch (err) {
              console.error("❌ Download failed:", err);
            } finally {
              setDownloadingId(null); // 👈 stop loader
            }
          }}
          sx={{
            "&:hover": {
              backgroundColor: primaryColor + '15',
              color: primaryColor,
            },
          }}
        >
          {downloadingId === row.id ? (
            <CircularProgress size={20} sx={{ color: primaryColor }} />
          ) : (
            <DownloadIcon sx={{ fontSize: 20, color: primaryColor }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  ) : (
    <Tooltip title={((row.fileType || row.file_type) || "").toUpperCase()}>
      <Box
        component="img"
        src={fileTypeIcon(row.fileType || row.file_type)}
        alt={row.fileType || row.file_type || "file"}
        sx={{ width: 22, height: 22 }}
      />
    </Tooltip>
  )}
</TableCell>


                <TableCell sx={dataStyle}>{formatDateTime(row.createdAt || row.created_at)}</TableCell>
                <TableCell sx={dataStyle}>{row.updated_at ? formatDateTime(row.updated_at) : "--"}</TableCell>

                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={(e) => openMenu(e, row)}
                    sx={{
                      "&:hover": {
                        backgroundColor: primaryColor + '15',
                        color: primaryColor,
                      },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination (backend) */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            shape="rounded"
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
                fontWeight: 500,
              },
              "& .Mui-selected": {
                backgroundColor: primaryColor + '20',
                color: primaryColor,
              },
              "& .MuiPaginationItem-root:hover": {
                backgroundColor: secondaryColor + '15',
                color: secondaryColor,
              },
            }}
          />
        </Box>

        {/* Row menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MenuItem
            onClick={() => {
              setViewRow(selectedRow);
              setViewDrawerOpen(true);
              closeMenu();
            }}
            sx={{ fontSize: 14, fontFamily: "Poppins, sans-serif" }}
          >
            {t("common.view") || "View"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setViewRow(selectedRow);
              setEditDrawerOpen(true);
              closeMenu();
            }}
            sx={{ fontSize: 14, fontFamily: "Poppins, sans-serif" }}
          >
            {t("common.edit") || "Edit"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDeleteDialogOpen(true);
              closeMenu();
            }}
            sx={{ 
              fontSize: 14, 
              fontFamily: "Poppins, sans-serif",
              color: "#d32f2f"
            }}
          >
            {t("common.delete") || "Delete"}
          </MenuItem>
        </Menu>
      </Box>
      </>
      )}
      
    </Box>
          <AddDocDrawer
  open={addDrawerOpen}
  onClose={() => setAddDrawerOpen(false)}
    onSuccess={fetchDocs}   // ✅ refresh list after add

/>
<EditDrawer
  open={editDrawerOpen}
  onClose={() => setEditDrawerOpen(false)}
  row={viewRow}
  onSuccess={fetchDocs}
/>
<ViewDrawer
  open={viewDrawerOpen}
  onClose={() => setViewDrawerOpen(false)}
  row={viewRow}
/>
<FilterDrawer
  open={filterDrawerOpen}
  onClose={() => setFilterDrawerOpen(false)}
  filters={filters}
  setFilters={setFilters}
  onApplyFilters={handleApplyFilters}
/>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: "Poppins, sans-serif" }}>
          {t("common.confirmDelete") || "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Poppins, sans-serif" }} fontSize="16px" fontWeight={400} color="#172B4D">
            {t("common.deleteConfirmMessage") || "Are you sure you want to delete this document? This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ fontFamily: "Poppins, sans-serif", textTransform: "none" }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{ fontFamily: "Poppins, sans-serif", textTransform: "none" }}
          >
            {deleting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("common.delete") || "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
</>
  );
}